import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api, withQuery, money, quantity, date, dateTime, isoDate, DOC_TYPE_LABEL } from '../api.js';
import { useAuth } from '../App.jsx';
import GroupPicker from '../components/GroupPicker.jsx';

const UNITS = ['бр.', 'кутия', 'пакет', 'кг', 'л', 'м', 'к-т'];

const emptyForm = {
  groupId: '', name: '', code: '', barcode: '', unit: 'бр.', vatRate: 20,
  isBatchTracked: false, weightKg: '', country: '', warranty: '',
  purchasePrice: '', wholesalePrice: '', retailPrice: '',
  minQty: '', maxQty: '', isActive: true, isWebVisible: false,
  webName: '', note: '', props: {},
};

export default function ProductView({ mode = 'view' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = mode === 'new';

  const [product, setProduct] = useState(null);
  const [groups, setGroups] = useState([]);
  const [properties, setProperties] = useState([]);
  const [movements, setMovements] = useState([]);
  // Нова стока от „Наличности“ идва с избраната група (?groupId=, 06.10.2026).
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(isNew
    ? { ...emptyForm, groupId: searchParams.get('groupId') ?? emptyForm.groupId }
    : null);
  const [editing, setEditing] = useState(isNew);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Смяната на името е отделно, незабавно действие — не чака „Запази" на
  // останалите полета, точно както в „Наличности" (виж StockTree.jsx).
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);

  const canWrite = user.role !== 'auditor';

  useEffect(() => {
    api.get('/api/product-groups').then(setGroups).catch(() => {});
    api.get('/api/properties').then(setProperties).catch(() => {});
  }, []);

  /*
   * Нова стока по образец (?from=, 12.10.2026): пълният картон, попълнен
   * с всичко от образеца — име, група, цени, бележка, характеристики.
   * Операторът сменя каквото трябва; нищо не се записва преди „Запази".
   */
  const [obrazec, setObrazec] = useState(null);
  useEffect(() => {
    const from = isNew ? searchParams.get('from') : null;
    if (!from) return;
    api.get(`/api/products/${from}`).then((p) => {
      setObrazec(p);
      setForm({ ...toForm(p), name: `${p.name} (копие)`, isActive: true, isWebVisible: false, webName: '' });
    }).catch((e) => setError(e.message));
  }, [isNew]);

  useEffect(() => {
    if (isNew) return;
    setProduct(null);
    setRenaming(false);
    Promise.all([
      api.get(`/api/products/${id}`),
      api.get(withQuery('/api/reports/movements', { productId: id, limit: 60 })),
    ])
      .then(([p, m]) => { setProduct(p); setForm(toForm(p)); setMovements(m); })
      .catch((e) => setError(e.message));
  }, [id, isNew]);

  function set(patch) { setForm((f) => ({ ...f, ...patch })); }

  /*
   * Сървърът пази от дублиране в същата група (`PATCH /api/products/:id`
   * връща 409 „В тази група вече има стока с това наименование") — тук
   * само се показва отговорът му.
   */
  async function renameProduct() {
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === product.name) { setRenaming(false); return; }
    setRenameBusy(true);
    setError(null);
    try {
      const after = await api.patch(`/api/products/${id}`, { name: trimmed });
      const fresh = { ...product, ...after };
      setProduct(fresh);
      setForm((f) => ({ ...f, name: fresh.name }));
      setRenaming(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setRenameBusy(false);
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        groupId: Number(form.groupId),
        name: form.name.trim(),
        code: blank(form.code),
        barcode: blank(form.barcode),
        unit: form.unit || 'бр.',
        vatRate: Number(form.vatRate) || 0,
        isBatchTracked: !!form.isBatchTracked,
        weightKg: numOrNull(form.weightKg),
        country: blank(form.country),
        warranty: blank(form.warranty),
        purchasePrice: numOrNull(form.purchasePrice),
        wholesalePrice: numOrNull(form.wholesalePrice),
        retailPrice: numOrNull(form.retailPrice),
        minQty: numOrNull(form.minQty),
        maxQty: numOrNull(form.maxQty),
        isActive: !!form.isActive,
        isWebVisible: !!form.isWebVisible,
        webName: blank(form.webName),
        note: blank(form.note),
        properties: properties.map((p) => ({
          propertyId: p.id, value: blank(form.props[String(p.id)]),
        })),
      };

      if (isNew) {
        const created = await api.post('/api/products', payload);
        navigate(`/products/${created.id}`, { replace: true });
      } else {
        const updated = await api.patch(`/api/products/${id}`, payload);
        // PATCH връща реда на стоката; характеристиките се четат наново.
        const fresh = await api.get(`/api/products/${id}`);
        setProduct(fresh);
        setForm(toForm(fresh));
        setEditing(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !form) return <div className="error">{error}</div>;
  if (!form) return <div className="empty">Зареждане…</div>;

  const groupName = groups.find((g) => String(g.id) === String(form.groupId))?.name;

  /*
   * Доставната цена идва от партидите, а не от полето по картона.
   *
   * Полето пази каквото е заварила старата програма и е известно
   * остаряло за стотици стоки (виж StockTree.jsx). Щом има партиди с
   * наличност, среднопретеглената от тях е истинската платена цена —
   * полето по картона остава само за стоки без партиди.
   */
  const partidnaCena = (() => {
    const s = (product?.batches ?? []).filter((b) => Number(b.quantity) > 0);
    const kolichestvo = s.reduce((a, b) => a + Number(b.quantity), 0);
    if (!kolichestvo) return null;
    const stoinost = s.reduce((a, b) => a + Number(b.quantity) * Number(b.purchase_price ?? 0), 0);
    return Math.round((stoinost / kolichestvo) * 10000) / 10000;
  })();

  return (
    <>
      <div className="page-head">
        {isNew ? (
          <h1>
            Нова стока
            {obrazec && <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}> по образец на {obrazec.name}</span>}
          </h1>
        ) : renaming ? (
          <>
            <input autoFocus maxLength={200} value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') renameProduct();
                if (e.key === 'Escape') setRenaming(false);
              }}
              style={{ fontSize: 20, fontWeight: 600, minWidth: 220 }} />
            <button className="link" disabled={renameBusy} onClick={renameProduct}>запиши</button>
            <button className="link" disabled={renameBusy} onClick={() => setRenaming(false)}>
              откажи
            </button>
          </>
        ) : (
          <>
            <h1>{product?.name}</h1>
            {canWrite && (
              <button className="link"
                onClick={() => { setRenameValue(product.name); setRenaming(true); }}>
                смяна
              </button>
            )}
          </>
        )}
        {!isNew && groupName && <span className="badge">{groupName}</span>}
        {form.isBatchTracked && <span className="badge warn">на партиди</span>}
        {!form.isActive && <span className="badge muted">неактивна</span>}
        <div className="spacer" />
        {!editing && canWrite && (
          <button className="primary" onClick={() => setEditing(true)}>Редактирай</button>
        )}
        {editing && !isNew && (
          <button onClick={() => { setForm(toForm(product)); setEditing(false); setError(null); }}>
            Откажи
          </button>
        )}
        {editing && (
          <button className="primary" onClick={save}
            disabled={busy || form.name.trim().length < 2 || !form.groupId}>
            {busy ? 'Запазване…' : 'Запази'}
          </button>
        )}
        <Link to="/products">← към списъка</Link>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-head">Данни</div>
        <div className="card-body">
          <div className="form-grid">
            <Field label="Наименование" wide
              hint={!isNew ? 'сменя се от бутона до заглавието горе' : undefined}>
              <input value={form.name} onChange={(e) => set({ name: e.target.value })}
                disabled={!isNew || !editing} />
            </Field>
            <Field label="Група">
              {editing ? (
                <GroupPicker
                  label="— изберете —"
                  value={form.groupId}
                  valueLabel={groupName}
                  onPick={(id) => set({ groupId: id })}
                  onClear={() => set({ groupId: '' })}
                />
              ) : (
                <div className="field-static">{groupName ?? '—'}</div>
              )}
            </Field>
            <Field label="Каталожен номер">
              <input value={form.code} onChange={(e) => set({ code: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Бърз код">
              <input value={form.barcode} onChange={(e) => set({ barcode: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Мярка">
              <input list="units" value={form.unit} onChange={(e) => set({ unit: e.target.value })} disabled={!editing} />
              <datalist id="units">{UNITS.map((u) => <option key={u} value={u} />)}</datalist>
            </Field>
            <Field label="ДДС, %">
              <input type="number" step="0.01" min="0" max="100" value={form.vatRate}
                onChange={(e) => set({ vatRate: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Тегло, кг">
              <input type="number" step="0.001" min="0" value={form.weightKg}
                onChange={(e) => set({ weightKg: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Гаранция">
              <input value={form.warranty} onChange={(e) => set({ warranty: e.target.value })} disabled={!editing} />
            </Field>
            <Check label="Води се на партиди" checked={form.isBatchTracked} disabled={!editing}
              onChange={(v) => set({ isBatchTracked: v })} />
            <Check label="Активна" checked={form.isActive} disabled={!editing}
              onChange={(v) => set({ isActive: v })} />
            <Check label="Показвай в сайта" checked={form.isWebVisible} disabled={!editing}
              onChange={(v) => set({ isWebVisible: v })} />
          </div>

          {properties.length > 0 && (
            <>
              <div className="detail-section">Характеристики</div>
              <div className="form-grid">
                {properties.map((p) => (
                  <Field label={p.name} key={p.id}>
                    {/* Подсказки от старите стойности, но текстът е свободен. */}
                    <input list={`prop-${p.id}`} autoComplete="off" disabled={!editing}
                      value={form.props[String(p.id)] ?? ''}
                      onChange={(e) => set({ props: { ...form.props, [String(p.id)]: e.target.value } })} />
                    <datalist id={`prop-${p.id}`}>
                      {p.values.map((v) => <option key={v} value={v} />)}
                    </datalist>
                  </Field>
                ))}
              </div>
            </>
          )}

          <label className="field" style={{ marginTop: 14 }}>
            Забележка
            <textarea rows={2} value={form.note ?? ''} onChange={(e) => set({ note: e.target.value })} disabled={!editing} />
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          Базови цени
          <span className="hint" style={{ fontWeight: 400 }}>без ДДС, важат, ако складът няма своя цена</span>
        </div>
        <div className="card-body">
          <div className="form-grid">
            {partidnaCena !== null ? (
              <Field label="Доставна" hint="среднопретеглена по партидите с наличност">
                <div className="field-static"><b>{money(partidnaCena)}</b></div>
              </Field>
            ) : (
              <Field label="Доставна">
                <input type="number" step="0.0001" min="0" value={form.purchasePrice}
                  onChange={(e) => set({ purchasePrice: e.target.value })} disabled={!editing} />
              </Field>
            )}
            <Field label="Едро">
              <input type="number" step="0.0001" min="0" value={form.wholesalePrice}
                onChange={(e) => set({ wholesalePrice: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Дребно">
              <input type="number" step="0.0001" min="0" value={form.retailPrice}
                onChange={(e) => set({ retailPrice: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Минимално количество">
              <input type="number" step="0.001" min="0" value={form.minQty}
                onChange={(e) => set({ minQty: e.target.value })} disabled={!editing} />
            </Field>
            <Field label="Максимално количество">
              <input type="number" step="0.001" min="0" value={form.maxQty}
                onChange={(e) => set({ maxQty: e.target.value })} disabled={!editing} />
            </Field>
          </div>
        </div>
      </div>

      {!isNew && product && (
        <>
          <StockPrices product={product} canWrite={canWrite} onSaved={(rows) =>
            setProduct((p) => ({ ...p, stock: rows }))} />

          {product.batches?.length > 0 && (
            <div className="card">
              <div className="card-head">Партиди с наличност</div>
              <table className="compact">
                <thead>
                  <tr>
                    <th>Партида</th><th>Склад</th>
                    <th className="num">Количество</th>
                    <th className="num">Доставна цена</th>
                    <th>Доставчик</th>
                    <th>Годност</th>
                  </tr>
                </thead>
                <tbody>
                  {product.batches.map((b) => (
                    <tr key={b.id}>
                      <td>{b.batch_no}</td>
                      <td className="muted">{b.warehouse_name}</td>
                      <td className="num">{quantity(b.quantity)} {product.unit}</td>
                      <td className="num">
                        {b.purchase_price != null ? money(b.purchase_price, b.currency) : '—'}
                      </td>
                      <td className="muted">
                        {b.supplier_name ?? '—'}
                        {b.purchase_date && (
                          <span style={{ marginLeft: 6 }}>· {date(b.purchase_date)}</span>
                        )}
                      </td>
                      <td className="muted">{b.expiry_date ? date(b.expiry_date) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card">
            <div className="card-head">Движение</div>
            <div className="table-wrap">
              <table className="compact">
                <thead>
                  <tr>
                    <th>Кога</th><th>Основание</th><th>Документ</th>
                    <th>Контрагент</th><th>Склад</th>
                    <th className="num">Промяна</th><th className="num">След това</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id}>
                      <td>
                        {/* Датата на документа; внесеното по-късно (Генсофт) — с кога е внесено. */}
                        {m.doc_date && isoDate(new Date(m.moved_at)) !== m.doc_date
                          ? <>{date(m.doc_date)} <span className="muted" title={`въведено ${dateTime(m.moved_at)}`}>(внесено {date(m.moved_at)})</span></>
                          : dateTime(m.moved_at)}
                      </td>
                      <td>{DOC_TYPE_LABEL[m.reason] ?? reasonLabel(m.reason)}</td>
                      <td>
                        {m.doc_number
                          ? <Link to={`/documents?q=${m.doc_number}`}>{m.doc_number}</Link>
                          : <span className="muted">—</span>}
                      </td>
                      <td className="muted">{m.partner_name ?? '—'}</td>
                      <td className="muted">{m.warehouse_name}</td>
                      <td className="num" style={{ color: Number(m.quantity_delta) < 0 ? 'var(--danger)' : 'var(--ok)' }}>
                        {Number(m.quantity_delta) > 0 ? '+' : ''}{quantity(m.quantity_delta)}
                      </td>
                      <td className="num muted">{quantity(m.quantity_after)}</td>
                    </tr>
                  ))}
                  {movements.length === 0 && (
                    <tr><td colSpan={7} className="empty">Няма движения</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/** Цени и място за съхранение по складове. */
function StockPrices({ product, canWrite, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState(product.stock);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setRows(product.stock); }, [product.stock]);

  /*
   * Доставната цена по склад идва от партидите НА ТОЗИ склад, не от
   * заведеното в `stock.purchase_price` — същата логика като в
   * „Базови цени" горе и в „Наличности" (StockTree.jsx), само разбита
   * по склад: две партиди на една стока в различни складове може да са
   * с различна доставна цена.
   */
  function partidnaCenaZaSklad(warehouseId) {
    const s = (product.batches ?? [])
      .filter((b) => Number(b.quantity) > 0 && String(b.warehouse_id) === String(warehouseId));
    const kolichestvo = s.reduce((a, b) => a + Number(b.quantity), 0);
    if (!kolichestvo) return null;
    const stoinost = s.reduce((a, b) => a + Number(b.quantity) * Number(b.purchase_price ?? 0), 0);
    return Math.round((stoinost / kolichestvo) * 10000) / 10000;
  }

  function patch(warehouseId, p) {
    setRows((rs) => rs.map((r) => (r.warehouse_id === warehouseId ? { ...r, ...p } : r)));
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      for (const r of rows) {
        await api.put(`/api/products/${product.id}/stock-prices`, {
          warehouseId: r.warehouse_id,
          purchasePrice: numOrNull(r.purchase_price),
          position: blank(r.position),
        });
      }
      onSaved(rows.map((r) => ({ ...r })));
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        Наличности и цени по складове
        <div style={{ flex: 1 }} />
        {!editing && canWrite && rows.length > 0 && (
          <button onClick={() => setEditing(true)}>Промени цените</button>
        )}
        {editing && <button onClick={() => { setRows(product.stock); setEditing(false); }}>Откажи</button>}
        {editing && <button className="primary" onClick={save} disabled={busy}>Запази</button>}
      </div>
      {error && <div className="card-body"><div className="error">{error}</div></div>}
      <table className="compact">
        <thead>
          <tr>
            <th>Склад</th>
            <th className="num">Наличност</th>
            <th className="num">Доставна</th>
            <th className="num">Едро</th>
            <th className="num">Дребно</th>
            <th>Място</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const partidna = partidnaCenaZaSklad(s.warehouse_id);
            return (
            <tr key={s.warehouse_id}>
              <td>{s.warehouse_name}</td>
              <td className="num">{quantity(s.quantity)} {product.unit}</td>
              <td className="num" title={partidna != null ? 'среднопретеглена по партидите с наличност в този склад' : undefined}>
                {partidna != null
                  ? <b>{money(partidna)}</b>
                  : editing
                    ? <input className="num" style={{ width: 100 }} type="number" step="0.0001" min="0"
                        value={s.purchase_price ?? ''}
                        onChange={(e) => patch(s.warehouse_id, { purchase_price: e.target.value })} />
                    : money(s.purchase_price ?? product.purchase_price)}
              </td>
              {/*
                * Продажната цена по склад (`stock.wholesale_price`/
                * `retail_price`) не се гледа никъде при реална продажба
                * — sale-info пита само картона. Показването и редакцията
                * им тук лъжеха, все едно склад продава на друга цена.
                * Винаги картонът, без вход.
                */}
              {['wholesale_price', 'retail_price'].map((k) => (
                <td className="num" key={k}>{money(product[k])}</td>
              ))}
              <td>
                {editing
                  ? <input style={{ width: 110 }} value={s.position ?? ''}
                      onChange={(e) => patch(s.warehouse_id, { position: e.target.value })} />
                  : <span className="muted">{s.position ?? '—'}</span>}
              </td>
            </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="empty">Стоката още не е заведена в склад</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, hint, wide, children }) {
  return (
    <label className="field" style={wide ? { gridColumn: 'span 2' } : undefined}>
      {label}
      {children}
      {hint && <span className="hint">{hint}</span>}
    </label>
  );
}

/** Заключената кутийка се чете зле — в режим на преглед пишем „да/не". */
function Check({ label, checked, onChange, disabled }) {
  if (disabled) {
    return (
      <div className="field">
        {label}
        <div className="readonly-value">
          {checked ? <span className="badge ok">да</span> : <span className="badge muted">не</span>}
        </div>
      </div>
    );
  }
  return (
    <label style={{ display: 'flex', gap: 7, alignItems: 'center', alignSelf: 'end', paddingBottom: 8 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function toForm(p) {
  return {
    groupId: String(p.group_id ?? ''),
    name: p.name ?? '',
    code: p.code ?? '',
    barcode: p.barcode ?? '',
    unit: p.unit ?? 'бр.',
    vatRate: Number(p.vat_rate ?? 20),
    isBatchTracked: !!Number(p.is_batch_tracked),
    weightKg: p.weight_kg ?? '',
    country: p.country ?? '',
    warranty: p.warranty ?? '',
    purchasePrice: p.purchase_price ?? '',
    wholesalePrice: p.wholesale_price ?? '',
    retailPrice: p.retail_price ?? '',
    minQty: p.min_qty ?? '',
    maxQty: p.max_qty ?? '',
    isActive: !!Number(p.is_active),
    isWebVisible: !!Number(p.is_web_visible),
    webName: p.web_name ?? '',
    note: p.note ?? '',
    props: Object.fromEntries((p.properties ?? []).map((x) => [String(x.property_id), x.value])),
  };
}

const blank = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());
const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

function reasonLabel(reason) {
  return { transfer_in: 'Прехвърляне (вход)', transfer_out: 'Прехвърляне (изход)' }[reason] ?? reason;
}
